import type { VoidAsyncResult } from "@/cache/AsyncResult";
import type { AnimationPrefetchContext } from "@/layers";
import { _debug_all_layers_from_used_scenes } from "@/layers/Compositor";
import {
  buildAnimationPrefetchContext,
  setCurrentAnimationPrefetchContext,
} from "@/models/time-control/animationPrefetchContext";
import type { AnimationPrefetchMode } from "@/reducer/PrefetchState";
import type { DateTime, Duration, Interval } from "luxon";

type FrameStatus = "pending" | "loading" | "ready";

type Frame = {
  timestamp: DateTime;
  status: FrameStatus;
};

type PrefetchPoolCallbacks = {
  onSliceStart?: (frameTimestamps: DateTime[]) => void;
  onSliceReady?: (frameTimestamps: DateTime[]) => void;
};

type PrefetchPoolConfig = {
  interval: Interval;
  temporalResolution: Duration;
  anchorTime: DateTime;
  displayTime: DateTime;
  initialFrames: number;
  mode: AnimationPrefetchMode;
  seekerLength?: number;
  callbacks?: PrefetchPoolCallbacks;
};

export class PrefetchPool {
  private disposed = false;
  private readonly seekerLength: number;
  private readonly callbacks: PrefetchPoolCallbacks;
  private readonly frames: Frame[];
  private readonly animationPrefetchContext: AnimationPrefetchContext;
  private readonly initialFrames: number;
  private readonly activeCancels = new Set<NonNullable<VoidAsyncResult["cancel"]>>();
  private anchorIndex = 0;
  private displayIndex = 0;
  private mode: AnimationPrefetchMode;
  private processing = false;
  private rerunRequested = false;
  private frozenTargetEndIndex = 0;

  /**
   * The pool tracks frame status for the whole animation range, but only schedules work
   * inside the active window derived from the current anchor and mode.
   *
   * `seed_only`:
   * Load the initial window near the anchor and then stop.
   *
   * `fill_remaining`:
   * Load every remaining frame from the anchor to the end of the configured range.
   */
  constructor({
    interval,
    temporalResolution,
    anchorTime,
    displayTime,
    initialFrames,
    mode,
    seekerLength = 2,
    callbacks = {},
  }: PrefetchPoolConfig) {
    this.animationPrefetchContext = buildAnimationPrefetchContext({
      windowStart: interval.start,
      windowEnd: interval.end,
      temporalResolution,
    });
    this.frames = this.animationPrefetchContext.frameTimestamps.map((time) => ({
      timestamp: time,
      status: "pending" as const,
    }));
    setCurrentAnimationPrefetchContext(this.animationPrefetchContext);
    this.initialFrames = initialFrames;
    this.mode = mode;
    this.seekerLength = seekerLength;
    this.callbacks = callbacks;
    this.anchorIndex = this.findAnchorIndex(anchorTime);
    this.displayIndex = this.findAnchorIndex(displayTime);
    this.frozenTargetEndIndex = this.getSeedTargetEndIndex(this.anchorIndex);
  }

  /**
   * Re-anchor the scheduling window without discarding already ready frames.
   * This is used when playback advances or the user selects a different target time.
   */
  updateAnchor(anchorTime: DateTime) {
    this.anchorIndex = this.findAnchorIndex(anchorTime);
    if (this.mode === "seed_only") {
      this.frozenTargetEndIndex = this.getSeedTargetEndIndex(this.anchorIndex);
    }
  }

  updateDisplayTime(displayTime: DateTime) {
    this.displayIndex = this.findAnchorIndex(displayTime);
  }

  /**
   * Switching back to `seed_only` freezes the window at what is already contiguous or
   * at the configured seed size, whichever is larger from the current anchor.
   */
  updateMode(mode: AnimationPrefetchMode) {
    if (this.mode === mode) {
      return;
    }

    this.mode = mode;
    if (mode === "seed_only") {
      this.frozenTargetEndIndex = this.getFrozenTargetEndIndex();
    }
  }

  /**
   * Start or resume scheduling. Concurrent calls are collapsed into a single rerun so the
   * caller can safely kick the pool whenever intent or anchor changes.
   */
  process() {
    if (this.disposed) {
      return;
    }
    if (this.processing) {
      this.rerunRequested = true;
      return;
    }

    this.processing = true;
    this.runNextSlice();
  }

  dispose() {
    if (this.disposed) {
      return;
    }

    this.disposed = true;
    for (const cancel of this.activeCancels) {
      cancel();
    }
    this.activeCancels.clear();
  }

  /**
   * Fetch the next batch and recurse until the current target window is fully covered.
   */
  private runNextSlice() {
    if (this.disposed) {
      this.finishProcessing();
      return;
    }

    const frames = this.selectNextSlice();
    if (!frames.length) {
      this.finishProcessing();
      return;
    }

    this.callbacks.onSliceStart?.(frames.map((frame) => frame.timestamp));

    Promise.all(frames.map((frame) => this.fetchFrame(frame))).then(() => {
      if (this.disposed) {
        this.finishProcessing();
        return;
      }

      this.callbacks.onSliceReady?.(frames.map((frame) => frame.timestamp));
      this.runNextSlice();
    });
  }

  private finishProcessing() {
    this.processing = false;
    if (this.rerunRequested && !this.disposed) {
      this.rerunRequested = false;
      this.process();
    }
  }

  /**
   * Select pending frames inside the current target window only. `fill_remaining`
   * first keeps a hot seed window from the rendered frame, then continues with the
   * explicit anchor-to-end pass and finally wraps to earlier missing frames.
   * Frames that are already loading or ready are skipped so the pool never refetches work it owns.
   */
  private selectNextSlice() {
    if (this.mode === "fill_remaining") {
      return this.selectWrappedSlice();
    }

    const framesToFetch: Frame[] = [];
    const targetEndIndex = this.getTargetEndIndex();
    if (targetEndIndex < this.anchorIndex) {
      return framesToFetch;
    }

    for (let index = this.anchorIndex; index <= targetEndIndex; index += 1) {
      const frame = this.frames[index];
      if (!frame || frame.status !== "pending") {
        continue;
      }

      frame.status = "loading";
      framesToFetch.push(frame);

      if (framesToFetch.length >= this.seekerLength) {
        break;
      }
    }

    return framesToFetch;
  }

  private selectWrappedSlice() {
    const framesToFetch: Frame[] = [];

    this.collectPendingFrames(framesToFetch, this.displayIndex, this.getSeedTargetEndIndex(this.displayIndex));
    if (framesToFetch.length >= this.seekerLength) {
      return framesToFetch;
    }

    this.collectPendingFrames(framesToFetch, this.anchorIndex, this.frames.length - 1);
    if (framesToFetch.length >= this.seekerLength) {
      return framesToFetch;
    }

    this.collectPendingFrames(framesToFetch, 0, this.anchorIndex - 1);
    return framesToFetch;
  }

  private collectPendingFrames(framesToFetch: Frame[], startIndex: number, endIndex: number) {
    if (startIndex > endIndex) {
      return;
    }

    for (let index = startIndex; index <= endIndex; index += 1) {
      const frame = this.frames[index];
      if (!frame || frame.status !== "pending") {
        continue;
      }

      frame.status = "loading";
      framesToFetch.push(frame);

      if (framesToFetch.length >= this.seekerLength) {
        return;
      }
    }
  }

  /**
   * `seed_only` stops after the initial window. `fill_remaining` keeps the target window
   * open until the end of the animation range.
   */
  private getTargetEndIndex() {
    if (!this.frames.length) {
      return -1;
    }

    if (this.mode === "seed_only") {
      return Math.min(this.frozenTargetEndIndex, this.frames.length - 1);
    }

    return this.frames.length - 1;
  }

  /**
   * When the pool is forced back into `seed_only`, keep any already contiguous frames
   * available instead of shrinking the visible buffer backwards.
   */
  private getFrozenTargetEndIndex() {
    const tailIndex = this.getContiguousBufferedTailIndex();
    if (tailIndex >= this.anchorIndex) {
      return tailIndex;
    }

    return this.getSeedTargetEndIndex(this.anchorIndex);
  }

  private getSeedTargetEndIndex(anchorIndex: number) {
    return Math.min(this.frames.length - 1, anchorIndex + this.initialFrames - 1);
  }

  /**
   * Only contiguous frames from the active anchor are considered buffered for playback.
   * A gap ends the usable window even if later frames are already ready.
   */
  private getContiguousBufferedTailIndex() {
    let index = this.anchorIndex;
    while (index < this.frames.length) {
      const status = this.frames[index]?.status;
      if (status !== "loading" && status !== "ready") {
        break;
      }
      index += 1;
    }

    return index - 1;
  }

  /**
   * Snap an arbitrary target time onto the discrete animation frame grid by choosing the
   * latest frame at or before the requested timestamp.
   */
  private findAnchorIndex(anchorTime: DateTime) {
    if (!this.frames.length) {
      return 0;
    }

    for (let index = this.frames.length - 1; index >= 0; index -= 1) {
      if (+this.frames[index].timestamp <= +anchorTime) {
        return index;
      }
    }

    return 0;
  }

  /**
   * A frame is marked ready only after every active layer reports that rebuffering for
   * that timestamp has finished.
   */
  private fetchFrame(frame: Frame) {
    return new Promise<void>((resolve) => {
      if (this.disposed) {
        resolve();
        return;
      }

      const loads: VoidAsyncResult[] = [];
      for (const layer of _debug_all_layers_from_used_scenes()) {
        const load = layer.fetchData(frame.timestamp, this.animationPrefetchContext);
        loads.push(load);
        if (load.cancel) {
          this.activeCancels.add(load.cancel);
        }
      }

      Promise.allSettled(loads.map((load) => load.asynchronous)).finally(() => {
        for (const load of loads) {
          if (load.cancel) {
            this.activeCancels.delete(load.cancel);
          }
        }
      });

      const checkRebufferingStatus = () => {
        if (this.disposed) {
          resolve();
          return;
        }

        if (!this.isFrameRebuffering(frame)) {
          frame.status = "ready";
          resolve();
          return;
        }

        requestAnimationFrame(checkRebufferingStatus);
      };

      checkRebufferingStatus();
    });
  }

  private isFrameRebuffering(frame: Frame) {
    return _debug_all_layers_from_used_scenes().reduce((isRebuffering, layer) => {
      if (layer.isRebuffering(frame.timestamp) || isRebuffering) {
        return true;
      }

      return false;
    }, false);
  }
}
